import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import sh from "bundles/common/utils/sh";
import DashboardMetricCard from "./dashboard/DashboardMetricCard";
import ActivityFeedCard from "./dashboard/ActivityFeedCard";
import TopPerformersCard from "./dashboard/TopPerformersCard";
import InternalUserDrawer from "./common/InternalUserDrawer";
import CoordinatorUsersTable from "./dashboard/CoordinatorUsersTable";
import DateRangeSelect from "./common/DateRangeSelect";

// keyframes must be defined outside of the component to avoid re-rendering
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const T = {
  bg: "hsl(140, 10%, 95%)",
  bg2: "#ffffff",
  bg3: "hsl(140, 8%, 90%)",
  bg4: "hsl(140, 6%, 86%)",
  fg: "hsl(150, 30%, 12%)",
  primary: "hsl(150, 38%, 17%)",
  primaryFg: "hsl(80, 97%, 72%)",
  dim: "hsl(150, 10%, 46%)",
  border: "hsl(140, 8%, 88%)",
  borderBright: "hsl(140, 8%, 82%)",
  green: "hsl(142, 71%, 45%)",
  red: "hsl(0, 72%, 51%)",
  blue: "hsl(217, 91%, 55%)",
  amber: "hsl(38, 92%, 50%)",
  teal: "hsl(174, 60%, 45%)",
};

const PERIODS = ["daily", "weekly", "monthly", "custom"];

const COMPARISON_LABELS = { daily: "vs yesterday", weekly: "vs last week", monthly: "vs last month", custom: "vs previous range" };

function outcomeLabel(outcome) {
  if (!outcome) return outcome;
  return outcome.replace(/_/g, " ");
}

function outcomeDotColor(outcome) {
  if (outcome === "interested" || outcome === "signed_up") return T.green;
  if (outcome === "sign_up_later") return "hsl(262, 65%, 55%)";
  if (outcome === "follow_up_today" || outcome === "follow_up_later") return T.amber;
  if (outcome === "no_answer" || outcome === "voicemail") return T.red;
  if (outcome === "applied") return T.blue;
  if (outcome === "email_sent") return T.blue;
  return T.dim;
}

const CoordinatorDashboard = () => {
  const currentInternalUser = useSelector((state) => state.current_internal_user);

  const [period, setPeriod] = useState("daily");
  const [customRange, setCustomRange] = useState({ startDate: null, endDate: null });
  const [stats, setStats] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [users, setUsers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activities, setActivities] = useState([]);
  const [atRiskCompanies, setAtRiskCompanies] = useState([]);
  const [atRiskLoading, setAtRiskLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const fetchDashboard = useCallback(() => {
    const params = { period };
    if (period === "custom" && customRange.startDate && customRange.endDate) {
      params.start_date = customRange.startDate;
      params.end_date = customRange.endDate;
    }
    sh.get("/internal/coordinator/dashboard", { params })
      .then((res) => {
        setStats(res.data.stats || null);
        setComparison(res.data.comparison || null);
        setUsers(Array.isArray(res.data.users) ? res.data.users : []);
        setLeaderboard(Array.isArray(res.data.leaderboard) ? res.data.leaderboard : []);
      })
      .catch(() => {});
  }, [period, customRange]);

  const fetchStaticData = useCallback(() => {
    sh.get("/internal/coordinator/dashboard/static")
      .then((res) => {
        setActivities(Array.isArray(res.data.activities) ? res.data.activities : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (period === "custom" && (!customRange.startDate || !customRange.endDate)) return;
    fetchDashboard();
  }, [fetchDashboard, period, customRange]);

  useEffect(() => {
    fetchStaticData();
  }, [fetchStaticData]);

  const topPerformers = useMemo(() => leaderboard.slice(0, 5), [leaderboard]);

  const role = currentInternalUser?.role;
  if (role !== "admin" && role !== "coordinator") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", flexDirection: "column" }}>
        <Typography sx={{ fontSize: 24, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.fg }}>Access denied</Typography>
        <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, mt: 1 }}>
          You don&apos;t have permission to view this page.
        </Typography>
      </Box>
    );
  }

  const delays = ["0s", "0.1s", "0.2s", "0.3s"];

  return (
    <Box
      sx={{
        backgroundColor: T.bg,
        minHeight: "100vh",
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": { backgroundColor: T.bg4, borderRadius: 3 },
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: "auto", px: "32px", py: "32px", display: "flex", flexDirection: "column", gap: "24px" }}>
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            animation: `${fadeUp} 0.5s ease both`,
            animationDelay: delays[0],
          }}
        >
          <Box>
            <Typography component="h1" sx={{ fontSize: 28, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.fg, lineHeight: 1.2 }}>
              Team Performance
            </Typography>
            <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, mt: "6px" }}>
              Calls, sourcing, and daily output for every member of the team
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {period === "custom" && (
              <DateRangeSelect
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={(range) => setCustomRange(range)}
              />
            )}
            <Box
              sx={{
                display: "flex",
                backgroundColor: T.bg3,
                borderRadius: "8px",
                p: "2px",
                border: `1px solid ${T.border}`,
              }}
            >
              {PERIODS.map((p) => (
                <Box
                  key={p}
                  component="button"
                  onClick={() => setPeriod(p)}
                  sx={{
                    all: "unset",
                    px: "16px",
                    py: "6px",
                    fontSize: 12,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 150ms ease",
                    color: period === p ? T.fg : T.dim,
                    backgroundColor: period === p ? T.bg2 : "transparent",
                    boxShadow: period === p ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                    "&:hover": { color: T.fg },
                  }}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* STAT CARDS */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "16px",
            animation: `${fadeUp} 0.5s ease both`,
            animationDelay: delays[1],
          }}
        >
          <DashboardMetricCard
            title="Total calls"
            value={stats?.total_calls ?? 0}
            delta={comparison?.total_calls ?? 0}
            comparisonLabel={COMPARISON_LABELS[period]}
            barColor={T.teal}
          />
          <DashboardMetricCard
            title="Candidates created"
            value={stats?.candidates_created ?? 0}
            delta={comparison?.candidates_created ?? 0}
            comparisonLabel={COMPARISON_LABELS[period]}
            barColor={T.green}
          />
          <DashboardMetricCard
            title="Applications created"
            value={stats?.applications_created ?? 0}
            delta={comparison?.applications_created ?? 0}
            comparisonLabel={COMPARISON_LABELS[period]}
            barColor={T.blue}
          />
          <DashboardMetricCard
            title="Sign rate"
            value={stats?.sign_rate ?? 0}
            suffix="%"
            delta={comparison?.sign_rate ?? 0}
            comparisonLabel={COMPARISON_LABELS[period]}
            barColor={T.red}
          />
        </Box>

        {/* MIDDLE ROW */}
        <Box
          sx={{
            animation: `${fadeUp} 0.5s ease both`,
            animationDelay: delays[2],
          }}
        >
          {/* Internal Users Table */}
          <CoordinatorUsersTable rows={users} onUserClick={setSelectedUserId} />
        </Box>

        {/* BOTTOM ROW */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            animation: `${fadeUp} 0.5s ease both`,
            animationDelay: delays[3],
          }}
        >
          {/* Top Performers */}
          <TopPerformersCard
            leaderboard={topPerformers}
            period={period}
            onUserClick={(userId) => setSelectedUserId(userId)}
          />

          {/* Live Activity Feed */}
          <ActivityFeedCard initialActivities={activities} />
        </Box>
      </Box>

      {/* Drawers */}
      <InternalUserDrawer
        selectedUserId={selectedUserId}
        period={period}
        startDate={customRange.startDate}
        endDate={customRange.endDate}
        onClose={() => setSelectedUserId(null)}
        onArchived={() => { fetchDashboard(); fetchStaticData(); }}
      />
    </Box>
  );
};

export default CoordinatorDashboard;
